/**
 * Dashboard shell (PRIVATE fork) — a top-level page (EXTRA_PAGES) that hosts the
 * letpot-api admin modules built on @greenhouse/crud. Picks the active workspace
 * (X-Workspace) and switches between module CrudPages.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Select, EmptyState } from '../../components/ui';
import { LayoutDashboard, Cloud } from '../../lib/icons';
import { fetchWorkspaces, type WorkspaceInfo } from '../../lib/workspaces-api';
import { useActiveWorkspace, setActiveWorkspace, getActiveWorkspace } from '../../lib/dashboard/workspace';
import { DashboardUsers } from './modules/users';
import { DashboardFaq } from './modules/faq';
import { DashboardProducts } from './modules/products';

const MODULES: Array<{ key: string; label: string; render: () => ReactNode }> = [
  { key: 'users', label: 'Users', render: () => <DashboardUsers /> },
  { key: 'faq', label: 'FAQ', render: () => <DashboardFaq /> },
  { key: 'products', label: 'Products', render: () => <DashboardProducts /> },
];

export function DashboardPage({ subPath }: { subPath: string }) {
  const active = useActiveWorkspace();
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([]);

  useEffect(() => {
    fetchWorkspaces().then((ws) => {
      setWorkspaces(ws);
      // Default to the first bound workspace if none selected yet.
      if (!getActiveWorkspace()) {
        const firstBound = ws.find((w) => w.binding);
        if (firstBound) setActiveWorkspace(firstBound.id);
      }
    });
  }, []);

  const bound = workspaces.filter((w) => w.binding);
  const moduleKey = subPath.split('/').filter(Boolean)[0] || MODULES[0].key;
  const activeModule = MODULES.find((m) => m.key === moduleKey) ?? MODULES[0];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header: workspace picker + module tabs */}
      <div className="border-b border-edge bg-surface-raised px-3 md:px-4 py-2.5 flex-shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <Cloud size={15} className="text-fg-muted" />
          <Select size="sm" className="w-auto" value={active} onChange={(e) => setActiveWorkspace(e.target.value)}>
            <option value="">Select workspace…</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id} disabled={!w.binding}>
                {w.name}
                {w.binding ? '' : ' (unbound)'}
              </option>
            ))}
          </Select>
        </div>
        <nav className="flex items-center gap-1">
          {MODULES.map((m) => (
            <button
              key={m.key}
              onClick={() => {
                window.location.hash = `#/dashboard/${m.key}`;
              }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                m.key === activeModule.key
                  ? 'bg-surface-sunken text-fg font-medium'
                  : 'text-fg-muted hover:text-fg hover:bg-surface-sunken'
              }`}
            >
              {m.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 md:px-4 py-4">
        {!active ? (
          <EmptyState
            icon={LayoutDashboard}
            title={bound.length === 0 ? 'No workspace bound' : 'Select a workspace'}
            description={
              bound.length === 0
                ? 'Bind a LetPot Admin workspace in Settings → LetPot Admin, then pick it here.'
                : 'Choose a workspace above to load its dashboard.'
            }
          />
        ) : (
          activeModule.render()
        )}
      </div>
    </div>
  );
}
